import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useCatalog } from './catalogContext';

const CartContext = createContext(null);
const CART_KEY = 'ds_cart';
const HOLD_KEY = 'ds_cart_hold';

const readStoredCart = () => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(CART_KEY) || '[]');
        if (!Array.isArray(stored)) {
            return [];
        }
        return stored.filter((entry) => entry?.item?.id && Number(entry.quantity) > 0);
    } catch (error) {
        return [];
    }
};

const readStoredHold = () => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(HOLD_KEY) || 'null');
        if (!stored?.token) {
            return { token: '', expires: null };
        }
        return { token: String(stored.token), expires: Number(stored.expires) || null };
    } catch (error) {
        return { token: '', expires: null };
    }
};

const lineMax = (item, inCart = 0) => {
    if (typeof item?.available === 'number') {
        return Math.max(0, item.available + inCart);
    }
    return Math.max(0, Number(item?.stock || 0));
};

export const CartProvider = ({ children }) => {
    const { getShopItem, refresh } = useCatalog();
    const [cart, setCart] = useState(readStoredCart);
    const [cartOpen, setCartOpen] = useState(false);
    const [hold, setHold] = useState(readStoredHold);
    const [expired, setExpired] = useState(() => {
        const stored = readStoredHold();
        return Boolean(stored.expires && stored.expires <= Date.now() && readStoredCart().length);
    });
    const [busy, setBusy] = useState(false);
    const [cartError, setCartError] = useState('');
    const [now, setNow] = useState(Date.now());
    const holdRef = useRef(hold);
    const cartRef = useRef(cart);
    const revisionRef = useRef(0);
    const restoredRef = useRef(false);
    holdRef.current = hold;
    cartRef.current = cart;

    useEffect(() => {
        window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        window.localStorage.setItem(HOLD_KEY, JSON.stringify(hold));
    }, [hold]);

    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);

    useEffect(() => {
        if (hold.expires && hold.expires <= now && cart.length) {
            setExpired(true);
        }
    }, [hold.expires, now, cart.length]);

    const liveItem = (id, fallback) => getShopItem(id) || fallback;

    const persistHold = (data) => {
        const next = { token: data?.token || '', expires: data?.expires || null };
        holdRef.current = next;
        setHold(next);
        return next;
    };

    const postHold = useCallback(async (entries, token = holdRef.current.token) => {
        const revision = revisionRef.current;
        try {
            const response = await fetch('/api/holds', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    items: entries.map((entry) => ({ id: entry.item.id, quantity: entry.quantity })),
                }),
            });
            const data = await response.json().catch(() => ({}));
            // A verified payment can clear the bag while restoration is in flight.
            // Its stale response must not restore an already-paid reservation.
            if (revision !== revisionRef.current) {
                // Restoration may have created a fresh hold after the paid one was
                // released. Cancel that orphan through the server's safe lifecycle,
                // while leaving any newer active reservation entirely untouched.
                if (response.ok && data.ok === true && typeof data.token === 'string'
                    && /^[a-f0-9]{32}$/.test(data.token) && data.token !== holdRef.current.token) {
                    fetch(`/api/holds/${encodeURIComponent(data.token)}`, { method: 'DELETE' }).catch(() => {});
                }
                return null;
            }
            if (!response.ok || data.ok !== true) {
                const error = new Error(data.error || 'Could not reserve stock. Please try again.');
                error.status = response.status;
                error.code = data.code;
                error.remaining = data.remaining;
                throw error;
            }
            persistHold(entries.length ? data : { token: '', expires: null });
            setExpired(Boolean(entries.length && data.expires && data.expires <= Date.now()));
            setCartError('');
            refresh();
            return data;
        } catch (error) {
            if (revision !== revisionRef.current) return null;
            throw error;
        }
    }, [refresh]);

    useEffect(() => {
        if (restoredRef.current) return;
        restoredRef.current = true;
        const entries = cartRef.current;
        if (!entries.length) {
            return;
        }
        setBusy(true);
        // Stripe checkout can extend a hold beyond the locally stored 15 minutes.
        // Always ask the server before treating a restored reservation as expired.
        postHold(entries, holdRef.current.token)
            .catch((error) => { setExpired(true); setCartError(error.message || 'Could not restore your reservation. Please try again.'); })
            .finally(() => setBusy(false));
    }, [postHold]);

    const requireActiveHold = () => {
        if (expired && cart.length) {
            setCartError('Your reservation expired. Refresh the cart to try again.');
            setCartOpen(true);
            return false;
        }
        return true;
    };

    const addToCart = async (item, quantity = 1, openCart = true) => {
        if (!requireActiveHold()) {
            return;
        }
        const current = liveItem(item.id, item);
        const inCart = cart.find((entry) => entry.item.id === item.id)?.quantity || 0;
        if (current.soldOut && inCart === 0) {
            return;
        }
        const max = lineMax(current, inCart);
        const nextQty = Math.min(max, inCart + quantity);
        if (nextQty < 1 || nextQty === inCart) {
            return;
        }
        const nextCart = inCart
            ? cart.map((entry) =>
                entry.item.id === item.id
                    ? { ...entry, item: current, quantity: nextQty }
                    : { ...entry, item: liveItem(entry.item.id, entry.item) }
            )
            : [...cart.map((entry) => ({ ...entry, item: liveItem(entry.item.id, entry.item) })), { item: current, quantity: nextQty }];
        setBusy(true);
        try {
            if (!await postHold(nextCart)) return;
            setCart(nextCart);
            if (openCart) {
                setCartOpen(true);
            }
        } catch (error) {
            setCartError(error.message);
            if (openCart) {
                setCartOpen(true);
            }
        } finally {
            setBusy(false);
        }
    };

    const removeFromCart = async (id) => {
        const nextCart = cart.filter((entry) => entry.item.id !== id);
        setBusy(true);
        try {
            // Even an apparently expired hold may own an active Stripe session.
            // The server must expire or settle that session before editing the bag.
            if (!await postHold(nextCart)) return;
            setCart(nextCart);
        } catch (error) {
            setCartError(error.message);
        } finally {
            setBusy(false);
        }
    };

    const updateQuantity = async (id, quantity) => {
        if (quantity < 1) {
            await removeFromCart(id);
            return;
        }
        if (!requireActiveHold()) {
            return;
        }
        const current = liveItem(id);
        const inCart = cart.find((entry) => entry.item.id === id)?.quantity || 0;
        const max = lineMax(current || { available: 0, stock: 0 }, inCart);
        const nextQty = Math.min(max, quantity);
        const nextCart = cart.map((entry) => (
            entry.item.id === id
                ? { ...entry, item: current || entry.item, quantity: nextQty }
                : entry
        ));
        setBusy(true);
        try {
            if (!await postHold(nextCart)) return;
            setCart(nextCart);
        } catch (error) {
            setCartError(error.message);
        } finally {
            setBusy(false);
        }
    };

    const clearCart = useCallback(() => {
        const token = holdRef.current.token;
        revisionRef.current += 1;
        cartRef.current = [];
        setCart([]);
        persistHold({ token: '', expires: null });
        setExpired(false);
        setCartError('');
        if (token) {
            fetch(`/api/holds/${encodeURIComponent(token)}`, { method: 'DELETE' }).catch(() => {});
        }
        refresh();
    }, [refresh]);

    const refreshCart = async () => {
        const entries = cartRef.current;
        const token = holdRef.current.token;
        setBusy(true);
        setCartError('');
        try {
            // Global catalog availability excludes this bag's own reservation.
            // Reuse its token and complete contents before considering any trims.
            if (!await postHold(entries, token)) return;
        } catch (error) {
            const remaining = error.remaining;
            const isStockConflict = error.status === 409
                && error.code === 'STOCK_UNAVAILABLE'
                && remaining && typeof remaining === 'object' && !Array.isArray(remaining)
                && entries.every((entry) => Object.prototype.hasOwnProperty.call(remaining, entry.item.id)
                    && Number.isSafeInteger(remaining[entry.item.id]) && remaining[entry.item.id] >= 0);
            if (!isStockConflict) {
                setCartError(error.message || 'Could not refresh your reservation. Your bag is saved; please try again.');
                return;
            }
            const next = entries.map((entry) => ({
                ...entry, quantity: Math.min(entry.quantity, remaining[entry.item.id]),
            })).filter((entry) => entry.quantity > 0);
            try {
                // Keep the original bag and token until the replacement is accepted.
                if (!await postHold(next, token)) return;
                setCart(next);
                setCartError(next.length ? 'Your bag was adjusted to the quantities still available.' : 'Those pieces are no longer available.');
            } catch (retryError) {
                setCartError(retryError.message || error.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const cartCount = cart.reduce((sum, entry) => sum + entry.quantity, 0);
    const subtotal = cart.reduce((sum, entry) => {
        const current = liveItem(entry.item.id, entry.item);
        return sum + current.price * entry.quantity;
    }, 0);
    const getQuantity = (id) => cart.find((entry) => entry.item.id === id)?.quantity || 0;
    const expiresInMs = hold.expires && !expired ? Math.max(0, hold.expires - now) : 0;

    return (
        <CartContext.Provider
            value={{
                cart,
                cartOpen,
                setCartOpen,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                refreshCart,
                getQuantity,
                cartCount,
                subtotal,
                holdToken: hold.token,
                expired,
                expiresInMs,
                busy,
                cartError,
                lineMax,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};
